import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './GlacierVideoExploration.css';
import { images } from '../../../shared/assets';
import { useBrandHomePageImages } from '../../../../hooks/useBrandHomePageContent';
import { HandDrawnButton } from '../../../shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../../../shared/CTALink/CTALink';

interface DestinationData {
  id: number;
  image: string;
  tag: string;
  name: string;
  location: string;
  nights: string;
  link: string;
}

// Move destinations array inside component to use translations
const useDestinations = () => {
  const { t } = useTranslation();
  
  return [
    {
      id: 1,
      image: images.destinationsAtacama,
      tag: t('pages/home:exploration.destinations.atacama.tag'),
      name: t('pages/home:exploration.destinations.atacama.name'),
      location: t('pages/home:exploration.destinations.atacama.location'),
      nights: t('pages/home:exploration.destinations.atacama.nights'),
      link: "/atacama"
    },
    {
      id: 2,
      image: images.destinationsPatagonia,
      tag: t('pages/home:exploration.destinations.patagonia.tag'),
      name: t('pages/home:exploration.destinations.patagonia.name'),
      location: t('pages/home:exploration.destinations.patagonia.location'),
      nights: t('pages/home:exploration.destinations.patagonia.nights'),
      link: "/patagonia"
    },
    {
      id: 3,
      image: images.destinationsIguazu,
      tag: t('pages/home:exploration.destinations.iguazu.tag'),
      name: t('pages/home:exploration.destinations.iguazu.name'),
      location: t('pages/home:exploration.destinations.iguazu.location'),
      nights: t('pages/home:exploration.destinations.iguazu.nights'),
      link: "/iguazu"
    },
    {
      id: 4,
      image: images.destinationsSantaCatarina,
      tag: t('pages/home:exploration.destinations.santaCatarina.tag'),
      name: t('pages/home:exploration.destinations.santaCatarina.name'),
      location: t('pages/home:exploration.destinations.santaCatarina.location'),
      nights: t('pages/home:exploration.destinations.santaCatarina.nights'),
      link: "/santa-catarina"
    },
    {
      id: 5,
      image: images.destinationsMendoza,
      tag: t('pages/home:exploration.destinations.mendoza.tag'),
      name: t('pages/home:exploration.destinations.mendoza.name'),
      location: t('pages/home:exploration.destinations.mendoza.location'),
      nights: t('pages/home:exploration.destinations.mendoza.nights'),
      link: "/mendoza"
    }
  ];
};

interface TagProps {
  text: string;
}

const Tag: React.FC<TagProps> = ({ text }) => {
  return (
    <div className="glacier-video-exploration__tag">
      <div className="glacier-video-exploration__tag-border" />
      <div className="glacier-video-exploration__tag-text">
        <p>{text}</p>
      </div>
    </div>
  );
};

interface DestinationCardProps {
  destination: DestinationData;
}

const DestinationCard: React.FC<DestinationCardProps> = ({ destination }) => {
  return (
    <div className="glacier-video-exploration__card">
      <div className="glacier-video-exploration__card-image-container">
        <img
          src={destination.image}
          alt={`${destination.name} ${destination.location}`}
          className="glacier-video-exploration__card-image"
        />
        <Tag text={destination.tag} />
      </div>
      <div className="glacier-video-exploration__card-content">
        <div className="glacier-video-exploration__card-title">
          <p>
            {destination.location === "Patagonia" 
              ? destination.location 
              : `${destination.name} ${destination.location}`}
          </p>
        </div>
        <div className="glacier-video-exploration__card-actions">
          <CTALink 
            text="VISIT THE PROPERTY"
            type="secondary"
            color="awasi"
            href={destination.link}
            className="glacier-video-exploration__link"
          />
          <CTALink 
            text="BOOK YOUR STAY"
            type="primary"
            color="awasi"
            href="/contact"
            className="glacier-video-exploration__link"
          />
        </div>
      </div>
    </div>
  );
};

export const GlacierVideoExploration: React.FC = () => {
  const { t } = useTranslation();
  const destinations = useDestinations();
  const carouselRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  const handleSeeDestinations = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleArrowClick = () => {
    if (carouselRef.current) {
      // Scroll to the right by one card width (1000px + 20px gap)
      const scrollAmount = 1020;
      carouselRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Use the glacier video URL from assets
  const glacierVideoUrl = images.destinationsBgVideo;

  // Ensure video plays
  React.useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(err => {
        console.error('Glacier video autoplay failed:', err);
      });
    }
  }, [glacierVideoUrl]);

  return (
    <section className="glacier-video-exploration">
      {/* Meaningful Exploration Section Only */}
      <div className="glacier-video-exploration__content">
        <div className="glacier-video-exploration__text-content">
          <h2 className="glacier-video-exploration__title">
            {String(t('pages/home:exploration.title')).split(' ').map((word, index) => (
              <React.Fragment key={index}>
                {word}{' '}
                {index === 2 && <br />}
              </React.Fragment>
            ))}
          </h2>
          <div className="glacier-video-exploration__description-wrapper">
            <p className="glacier-video-exploration__description">
              {t('pages/home:exploration.description')}
            </p>
            <HandDrawnButton
              text={t('pages/home:exploration.cta')}
              onClick={handleSeeDestinations}
              variant="brand"
              color="awasi"
              animation="stroke-to-fill"
              size="normal"
              className="glacier-video-exploration__see-button"
            />
          </div>
        </div>

        {/* Carousel Section */}
        <div className="glacier-video-exploration__carousel-container">
          <div className="glacier-video-exploration__carousel" ref={carouselRef}>
            {destinations.map((destination) => (
              <DestinationCard key={destination.id} destination={destination} />
            ))}
          </div>
          
          {/* Hand-drawn Arrow - Positioned to indicate carousel scroll */}
          <div className="glacier-video-exploration__arrow" onClick={handleArrowClick}>
            <img 
              src={images.carouselArrow} 
              alt="Scroll to see more destinations" 
              className="glacier-video-exploration__arrow-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
};